import { DateObject } from "@app/models/DateObject";
import { Homeowner } from "@app/models/Homeowner";
import { Payment } from "@app/models/Payment";
import { PaymentMethod } from "@app/models/PaymentMethod";
import { PaymentRequest } from "@app/models/PaymentRequest";
import { Property } from "@app/models/Property";
import { AchTokenRequest } from "@app/models/ftni/AchTokenRequest";
import { StoreBase } from "@app/services/stores/shared/StoreBase";
import { Def } from "@card/common/definitions";
import { Product } from "@app/models/Product";
import { Injectable } from "@angular/core";

/** year: 2017, month: 12, date: 31 */
type USDAmount = number
type PropertyCouponIds = string
type YearMonthDate = { year: number, month: number, date: number }
type PropertyAmountDue = { propertyStr: PropertyCouponIds, amount: USDAmount }

export type CardUpdate = Def.CardPaymentMethodUpdate

type GuestState = {
    homeowner: Homeowner
    property: Property
    paymentMethod: PaymentMethod
    payment: Payment
    routingNumber: string
    viewedTc: boolean
    delayTodaysPayment: boolean
    paymentDate: DateObject
    redirectAmount: PropertyAmountDue
    propertyAmountDue: PropertyAmountDue
    achTokenRequest: AchTokenRequest
    products: Product[]
    cardUpdate: CardUpdate
    cardIsPrefillAddress: boolean
    paymentRequest: PaymentRequest;
    correlationId: string;
}


const log: typeof console.log = window.__karma__ ? () => { } : console.log.bind(console, "%cGuestPaymentStore", "color: #E52B50")

@Injectable()
export class GuestPaymentStore extends StoreBase<GuestState> {
    constructor() {
        super("hoapay_guestPayment_", sessionStorage)
    }

    getPaymentDate(): DateObject | undefined {
        const date = this.get("paymentDate")
        if (!date) return undefined
        return date;
    }

    setPaymentDate(value: DateObject) {
        this.set("paymentDate", value);
    }

    clearPaymentDate(): DateObject | undefined {
        return this.remove("paymentDate")
    }

    getHomeowner(): Homeowner {
        return this.get("homeowner")
    }

    clearHomeowner(): Homeowner {
        return this.remove("homeowner")
    }

    setHomeowner(value: Homeowner) {
        if (value.contactInformation && value.contactInformation.phones && value.contactInformation.phones[0].value) {
            value.contactInformation.phones[0].value = value.contactInformation.phones[0].value.replace(/[^0-9]+/g, '');
        }

        this.set("homeowner", value)
    }


    getProperty(): Property {
        return this.get("property")
    }

    clearProperty(): Property {
        return this.remove("property")
    }

    setProperty(value: Property) {
        const propertyStr = propertyToString(value)
        if (!propertyStr) {
            return console.error(new TypeError(`Attempted to set invalid property ${JSON.stringify(value)}`).stack)
        }
        const currProperty = this.getProperty()
        if (currProperty != null && propertyToString(currProperty) !== propertyStr) {
            this.clearPropertyAmountDue()
            this.clearRedirectAmount()
        }

        this.set("property", value)
    }


    getPaymentMethod(): PaymentMethod {
        return this.get("paymentMethod");
    }

    clearPaymentMethod(): PaymentMethod {
        return this.remove("paymentMethod");
    }

    setPaymentMethod(value: PaymentMethod) {
        this.set("paymentMethod", value);
    }

    /** For Card checkout */
    getCardUpdate(): CardUpdate {
        return this.get("cardUpdate");
    }

    clearCardUpdate(): CardUpdate {
        return this.remove("cardUpdate");
    }

    setCardUpdate(value: CardUpdate) {
        this.set("cardUpdate", value);
    }

    /** For Card checkout */
    getCardIsPrefillAddress(): boolean {
        return this.get("cardIsPrefillAddress");
    }

    clearCardIsPrefillAddress(): boolean {
        return this.remove("cardIsPrefillAddress");
    }

    setCardIsPrefillAddress(value: boolean) {
        this.set("cardIsPrefillAddress", value);
    }


    getAchTokenRequest(): AchTokenRequest {
        return this.get("achTokenRequest")
    }

    clearAchTokenRequest(): AchTokenRequest {
        return this.remove("achTokenRequest")
    }

    setAchTokenRequest(value: AchTokenRequest) {
        this.set("achTokenRequest", value)
    }


    getPaymentRequest(): PaymentRequest {
        return this.get("paymentRequest")
    }

    clearPaymentRequest(): PaymentRequest {
        return this.remove("paymentRequest")
    }

    setPaymentRequest(value: PaymentRequest) {
        this.set("paymentRequest", value)
    }

    getPayment(): Payment {
        return this.get("payment");
    }

    clearPayment(): Payment {
        return this.remove("payment");
    }

    setPayment(value: Payment) {
        this.set("payment", value);
    }

    getViewedTc() {
        return this.get("viewedTc");
    }

    clearViewedTc() {
        return this.remove("viewedTc");
    }

    setViewedTc(value: boolean) {
        this.set("viewedTc", value);
    }


    getRedirectAmount(): number | undefined {
        const redirectAmount = this.get("redirectAmount")
        if (!redirectAmount) return undefined
        const currProperty = this.getProperty()
        if (!currProperty || propertyToString(currProperty) !== redirectAmount.propertyStr) return undefined

        return redirectAmount.amount
    }

    clearRedirectAmount() {
        this.remove("redirectAmount")
    }

    setRedirectAmount(property: Property, value: number) {
        value = Number(value)

        const propertyString = propertyToString(property)
        const currPropertyString = propertyToString(this.get("property"))
        if (propertyString !== currPropertyString) {
            return console.error(new TypeError(`RedirectAmount being set for a different property than intended "set for": ${propertyString} != "curr": ${currPropertyString}`).stack)
        }

        if (!value || value < 0) {
            this.clearRedirectAmount()
            console.error(new Error(`Invalid redirect amount >>${value}<< for >>${propertyString}<<`).stack)
            return
        }

        this.set("redirectAmount", { amount: value, propertyStr: propertyString })
    }


    getPropertyAmountDue(): number | undefined {
        const amountDue = this.get("propertyAmountDue")
        if (!amountDue) return undefined
        if (propertyToString(this.getProperty()) !== amountDue.propertyStr) return undefined
        return amountDue.amount
    }

    clearPropertyAmountDue() {
        return this.remove("propertyAmountDue")
    }

    /** If the property is different than the current property, than the current property will be replaced */
    setPropertyAmountDue(property: Property, value: number) {
        value = Number(value)

        const propertyString = propertyToString(property)
        const currPropertyString = propertyToString(this.get("property"))
        if (propertyString !== currPropertyString) {
            if (propertyString) this.setProperty(property)
            else return console.error(new TypeError(`PropertyAmountDue being set for a different property than intended "set for": ${propertyString} != "curr": ${currPropertyString}`).stack)
        }

        if (!value || value < 0) {
            this.clearPropertyAmountDue()
            console.error(new Error(`Invalid property amount >>${value}<< for >>${propertyString}<<`).stack)
            return
        }

        this.set("propertyAmountDue", { amount: value, propertyStr: propertyString })
    }

    getDelayTodaysPayment() {
        return this.get("delayTodaysPayment");
    }

    clearDelayTodaysPayment() {
        return this.remove("delayTodaysPayment");
    }

    setDelayTodaysPayment(value: boolean) {
        this.set("delayTodaysPayment", value);
    }

    setProducts(value: Product[]){
        this.set("products", value)
    }

    getProducts(){
        return this.get("products")
    }

    clearProducts(): Product[] {
        return this.remove("products")
    }

    setCorrelationId(value: string){
        this.set("correlationId", value)
    }

    getCorrelationId(){
        return this.get("correlationId")
    }

    clearCorrelationId(){
        return this.remove("correlationId")
    }

    clearAllGuest() {
        this.clearHomeowner();
        this.clearProperty();
        this.clearPaymentMethod();
        this.clearAchTokenRequest();
        this.clearCardUpdate();
        this.clearCardIsPrefillAddress();
        this.clearPaymentRequest();
        this.clearPayment();
        this.clearViewedTc();
        this.clearPaymentDate();
        this.clearRedirectAmount();
        this.clearPropertyAmountDue();
        this.clearDelayTodaysPayment();
        this.clearProducts();
        this.clearCorrelationId();
        log("Cleared all guest values")
    }
}

function propertyToString(property: Property): PropertyCouponIds {
    try {
        return `${property.couponId}/${property.hoa.couponId}/${property.hoa.pmc.couponId}`
    } catch (err) {
        console.warn(`Malformed property`, property)
        console.error(`Invalid property passed to GuestPaymentStore`, err instanceof Error ? err.stack : err)
        // Return a random string so two unknown properties don't match
        return `UndefinedProperty rand:${(Date.now() * Math.random()).toString(36)}`
    }
}
